import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from '@atlaskit/spinner';
import styled from '@emotion/styled';
import ReCAPTCHAEnterprise from './ReCAPTCHAEnterprise';
import EmailIcon from '@atlaskit/icon/glyph/email';
import { colors, gridSize } from '@atlaskit/theme';

import FieldGroup from './FieldGroup';
import EmailContainer from './EmailContainer';
import PasswordContainer from './PasswordContainer';
import RestrictedEmailContainer from './RestrictedEmailContainer';
import TallButton from './TallButton';
import ReCaptchaContainer from './ReCaptchaContainer';
import FormError from './FormError';
import FormInfo from './FormInfo';
import { StyledSocialLoginButton } from './SocialLoginPanel';
import messages from './LoginForm.messages';
import {
  LoginFieldError,
  LoginGlobalError,
  LoginGlobalInfo,
  CheckUserNameRedirectType,
} from '../types';
import { ConsentCheckboxState } from '../utilities/marketing-consent';
import MarketingConsent from './MarketingConsentCheckbox';
import { Application, applications } from '../utilities/applications';
import { useAnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { PASSWORD_LEAKED_LINK } from './links';
import { MarketingConsentConfig } from '../reducers/marketing-consent-reducer';
import { usePrevious } from '../utilities/hooks';
import { ChallengeResponse, useRecaptcha } from '../hooks/useRecaptcha';
import { useSearchParams } from 'react-router-dom';
import { loginPageId } from '../containers/LoginPage';
import { recaptchaCheckUsernameFetchTokenInteraction } from '../browser-metrics/constants';
import { isCheckUsernameRecaptchaEnabledFF } from '../utilities/recaptcha';

export const OpeningSso = styled.div`
  align-items: center;
  color: ${colors.N90};
  display: flex;
  height: 40px;
  justify-content: space-between;
  padding: 0 8px;
`;

export const Form = styled.form`
  margin: 0;
`;

const InputsWrapper = styled.div`
  margin-bottom: ${gridSize()}px;
`;

const StyledMarketingConsent = styled(MarketingConsent)`
  padding-top: ${gridSize() * 2}px;
`;

interface ValidationErrors {
  email?: string;
  password?: string;
}

export type LoginFormProps = {
  application: Application | null;
  isCaptchaEnabledByConfig: boolean;
  isCaptchaRequired?: boolean;
  isErrorShown: boolean;
  redirectType: CheckUserNameRedirectType | null;
  isRedirecting?: boolean;
  isSecondStep?: boolean;
  isPasswordlessAllowed?: boolean;
  isLoading?: boolean;
  nextAttempt: number;
  onContinue: (username: string, challengeResponse?: ChallengeResponse | null) => void;
  onLogIn: (
    email: string,
    password: string,
    captchaValue: string | null | undefined,
    marketingConsent: ConsentCheckboxState
  ) => void;
  onPasswordlessLogIn: (email: string, captchaValue: string | null | undefined) => void;
  fieldErrors: LoginFieldError;
  email: string;
  error: LoginGlobalError | null | undefined;
  info: LoginGlobalInfo | null | undefined;
  onEditEmail: () => void;
  onValidationError: (errors: ValidationErrors) => void;
  onCaptchaFailure: () => void;
  onFormClick: () => void;
  recaptchaEnterpriseCheckboxKeySite: string;
  resetPasswordLink: string;
  restrictedEmail: boolean;
  marketingConsentConfig: MarketingConsentConfig | null | undefined;
  pageId: string;
  onEmailChange: (username: string) => void;
};

export const LoginForm = ({
  application,
  email,
  error,
  fieldErrors,
  info,
  isCaptchaEnabledByConfig,
  isCaptchaRequired,
  isErrorShown,
  isLoading,
  isPasswordlessAllowed,
  isRedirecting,
  isSecondStep,
  marketingConsentConfig,
  nextAttempt,
  onContinue,
  onEditEmail,
  onEmailChange,
  onFormClick,
  onLogIn,
  onPasswordlessLogIn,
  onValidationError,
  onCaptchaFailure,
  pageId,
  recaptchaEnterpriseCheckboxKeySite,
  redirectType,
  resetPasswordLink,
  restrictedEmail,
}: LoginFormProps) => {
  const intl = useIntl();
  const analyticsClient = useAnalyticsClient();
  const [password, setPassword] = useState<string>('');
  const [captchaValue, setCaptchaValue] = useState<string | null | undefined>(null);
  const [marketingConsent, setMarketingConsent] = useState<ConsentCheckboxState>({
    state: 'not_shown',
  });
  const [isCheckUsernameCaptchaLoading, setIsCheckUsernameCaptchaLoading] = useState(false);
  const [buttonClicked, setButtonClicked] = useState<'submit' | 'passwordless'>('submit');
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchParams] = useSearchParams();

  const isCheckUsernameRecaptchaEnabled = isCheckUsernameRecaptchaEnabledFF();
  const recaptchaType = error?.code === 'captchaScoreTooLow' ? 'checkbox' : 'score';

  const recaptcha = useRecaptcha({
    pageId,
    type: recaptchaType,
    enabledProp: isCheckUsernameRecaptchaEnabled,
    action: 'checkUsername',
  });

  const restrict = searchParams.get('restrict') === 'true';

  const fetchRecaptchaTokenAndSubmitContinue = async email => {
    try {
      setIsCheckUsernameCaptchaLoading(true);
      recaptchaCheckUsernameFetchTokenInteraction.start();
      const challengeResponse = await recaptcha.fetchToken('checkUsername');
      recaptchaCheckUsernameFetchTokenInteraction.stop({
        customData: { restrict },
      });
      onContinue(email, challengeResponse);
    } catch (error) {
      analyticsClient.errorShownEvent(loginPageId, 'recaptchaError', {
        errorMessage: error.message,
      });
      onCaptchaFailure();
    } finally {
      setIsCheckUsernameCaptchaLoading(false);
    }
  };

  // This code is intended to auto-submit the check-username request if `restrict=true`
  useEffect(() => {
    const handleMount = async () => {
      const emailWithDefault = email || '';

      if (!restrict || !emailWithDefault) {
        return;
      }

      if (!isCheckUsernameRecaptchaEnabled) {
        onContinue(emailWithDefault, null);
        return;
      }

      // we don't want to show the spinner if checkbox recaptcha is loading for the time being
      if (recaptchaType === 'score') {
        setIsCheckUsernameCaptchaLoading(true);
      }

      if (recaptcha.isLoaded && recaptchaType === 'score') {
        await fetchRecaptchaTokenAndSubmitContinue(emailWithDefault);
      }
    };

    void handleMount();

    // We only want this to run when the component mounts or
    // when recaptcha.isLoaded becomes true (if it was false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptcha.isLoaded]);

  const onEmailClick = useCallback(() => {
    setPassword('');
    onEditEmail();
  }, [onEditEmail]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    const trimmedEmail = email.trim();
    const emailEmpty = trimmedEmail === '';
    const passwordEmpty = password === '';
    const validationErrors: ValidationErrors = {};

    e.preventDefault();

    if (isLoading) {
      return;
    }

    if (emailEmpty) {
      validationErrors.email = formatMessage(messages.emailEmpty);
      inputRef.current?.focus();
    }

    setButtonClicked('submit');
    if (!isSecondStep) {
      analyticsClient.buttonClickedEvent(pageId, 'continueToLogin');
      if (!emailEmpty) {
        if (isCheckUsernameRecaptchaEnabled) {
          await fetchRecaptchaTokenAndSubmitContinue(trimmedEmail);
        } else {
          onContinue(trimmedEmail, null);
        }
      }
    } else {
      analyticsClient.buttonClickedEvent(pageId, 'login');
      if (passwordEmpty) {
        validationErrors.password = formatMessage(messages.passwordEmpty);
      } else if (!emailEmpty) {
        onLogIn(trimmedEmail, password, captchaValue, marketingConsent);
      }
    }

    if (Object.keys(validationErrors).length) {
      analyticsClient.errorShownEvent(loginPageId, 'validationError');
      onValidationError(validationErrors);
    }
  };

  const handlePasswordlessButtonClick = async () => {
    const trimmedEmail = email.trim();

    if (isLoading || !trimmedEmail) {
      return;
    }

    setButtonClicked('passwordless');
    onPasswordlessLogIn(trimmedEmail, captchaValue);
  };

  const handleCaptchaChange = useCallback((captchaValue: string | null | undefined) => {
    setCaptchaValue(captchaValue);
  }, []);

  const handleMarketingConsentChange = useCallback((consent: ConsentCheckboxState) => {
    setMarketingConsent(consent);
  }, []);

  const handlePasswordChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
  }, []);

  const buttonContent = () => {
    if (!isSecondStep) {
      return <FormattedMessage id="login.form.continue" defaultMessage="Continue" />;
    }
    if (isPasswordlessAllowed) {
      return (
        <FormattedMessage id="common.login.with.password" defaultMessage="Log in with password" />
      );
    }
    return <FormattedMessage id="common.login" defaultMessage="Log in" />;
  };

  const incorrectCredentialsErrorMessage = () => {
    if (nextAttempt > 4) {
      return (
        <FormattedMessage
          id="login.error.invalid.credentials.more.help.xml"
          defaultMessage="We've recently made changes to the login page. If you are having difficulty logging in, <reset-password-link>reset your password</reset-password-link>."
          values={{
            'reset-password-link': chunks => <a href={resetPasswordLink}>{chunks}</a>,
          }}
        />
      );
    } else if (application === 'trello') {
      return (
        <FormattedMessage
          id="login.error.invalid.credentials.trello.specific.help.xml"
          defaultMessage="Incorrect email address and / or password. If you recently migrated your Trello account to an Atlassian account, you will need to use your Atlassian account password. Alternatively, you can get help <reset-password-link>logging in</reset-password-link>."
          values={{
            'reset-password-link': chunks => <a href={resetPasswordLink}>{chunks}</a>,
          }}
        />
      );
    } else {
      return (
        <FormattedMessage
          id="login.error.invalid.credentials.xml"
          defaultMessage="Incorrect email address and / or password.{br}Do you need help <reset-password-link>logging in</reset-password-link>?"
          values={{
            br: <br />,
            'reset-password-link': chunks => <a href={resetPasswordLink}>{chunks}</a>,
          }}
        />
      );
    }
  };

  const passwordLeakedErrorMessage = () => (
    <FormattedMessage
      id="login.error.password.leaked.xml"
      defaultMessage="You must <reset-password-link>change your password</reset-password-link> to log in. Your current password has appeared in an external data breach and your Atlassian account is at risk. <password-leaked-link>Learn more</password-leaked-link>"
      values={{
        'reset-password-link': chunks => <a href={resetPasswordLink}>{chunks}</a>,
        'password-leaked-link': chunks => <a href={PASSWORD_LEAKED_LINK}>{chunks}</a>,
      }}
    />
  );

  const infoContent = () => {
    const { formatMessage } = intl;
    const applicationName = application ? applications[application].name : 'Product';
    const testId = 'login-info-test-id';

    if (info) {
      switch (info.code) {
        case 'migratingExistingUser': {
          const summary = formatMessage(messages.migrateAccountInfo, {
            application: applicationName,
          });
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        case 'existingUserSignupAttempt': {
          const summary = formatMessage(messages.existingUserSignupAttemptInfo);
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        default:
          return (
            <FormInfo id="login-info" data-testid={testId} isHidden={true}>
              {' '}
            </FormInfo>
          );
      }
    }
  };

  const errorContent = () => {
    if (error) {
      switch (error.code) {
        case 'captcha':
          return (
            <FormattedMessage
              id="login.error.captcha"
              defaultMessage="We are having trouble verifying reCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support."
            />
          );
        case 'captchaScoreTooLow':
          return (
            <FormattedMessage
              id="login.error.captcha.score_too_low"
              defaultMessage="We'd like to make sure it's really you. Please confirm you're not a robot."
              description="Message asking user to tick the reCAPTCHA checkbox and resubmit the form"
            />
          );
        case 'invalid_user_password':
          return incorrectCredentialsErrorMessage();
        case 'ambiguous_user':
          return (
            <FormattedMessage
              id="login.error.details.ambiguous"
              defaultMessage="It seems that you have multiple accounts associated with your email. Please contact your administrator to remove the duplicate."
            />
          );
        case 'no_email_on_remote_user':
          return (
            <FormattedMessage
              id="login.error.remote.user.no.email"
              defaultMessage="Your {username} user doesn&#39;t have an associated email address, so we can&#39;t create your Atlassian account. Please contact support."
              values={error && error.args}
            />
          );
        case 'bitbucket_username_is_not_email':
          return (
            <FormattedMessage
              id="login.error.remote.user.using.username"
              defaultMessage="You can no longer log in with a username. Use your primary email address {username} instead."
              values={error.args || {}}
            />
          );
        case 'username_is_not_email':
          return (
            <FormattedMessage
              id="login.error.email.not.username"
              defaultMessage="You must use your email address to log in. Did you mean to use {username}?"
              values={error.args || {}}
            />
          );
        case 'bitbucket_email_is_not_primary':
          return (
            <FormattedMessage
              id="login.error.remote.user.using.alias"
              defaultMessage="You can no longer log in with an email alias. Use your primary email address {username} instead."
              values={error.args || {}}
            />
          );
        case 'bitbucket_mfa_enforced':
          return (
            <FormattedMessage
              id="login.error.user.mfa.enforced"
              defaultMessage="Your organization wants you to secure your account by setting up two-step verification."
              values={error.args || {}}
            />
          );
        case 'bitbucket_mfa_required':
          return (
            <FormattedMessage
              id="login.error.user.mfa.required"
              defaultMessage="For added security, your organization wants you to log in with two-step verification."
              values={error.args || {}}
            />
          );
        case 'bitbucket_saml_user':
          return (
            <FormattedMessage
              id="login.error.saml.user"
              defaultMessage="For added security, your organization wants you to log in with single sign-on."
              values={error.args || {}}
            />
          );
        case 'email_is_not_primary':
          return (
            <FormattedMessage
              id="login.error.email.not.username"
              defaultMessage="You must use your email address to log in. Did you mean to use {username}?"
              values={error.args || {}}
            />
          );
        case 'too_many_logins':
          return (
            <FormattedMessage
              id="login.error.too.many.logins"
              defaultMessage="For security purposes, we&#39;ve temporarily locked your account due to multiple log in attempts within a short period of time. Please try again in a few minutes. If this issue persists, please contact Atlassian Support."
            />
          );
        case 'multiple_google_accounts':
          return (
            <FormattedMessage
              id="login.error.google.auth.contact.support"
              defaultMessage="We&#39;ve encountered problems signing you in with your Google Account. Please contact support to resolve this problem."
            />
          );
        case 'unable_to_verify_google_token':
          return (
            <FormattedMessage
              id="login.error.unable.to.verify.google.token"
              defaultMessage="We were unable to verify your Google Account. Please try again later."
            />
          );
        case 'google_email_change_not_allowed_by_auth_policy':
          return (
            <FormattedMessage
              id="login.error.google.email.change.not.allowed.by.auth.policy"
              defaultMessage="Sorry, we detected an email change that is not supported by your organization's authentication policy. Please log in with an email address that matches your account."
            />
          );
        case 'google_email_change_not_allowed_by_user_managed_status':
          return (
            <FormattedMessage
              id="login.error.google.email.change.not.allowed.by.user.managed.status"
              defaultMessage="We detected an email change that would remove you from your organization. Contact your organization admin to check your account is configured correctly or try to log in a different way."
            />
          );
        case 'social_login_missing_email':
          return (
            <FormattedMessage
              id="login.error.social.missing.email"
              defaultMessage={`We did not receive a valid email address from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'social_login_missing_email_verified':
          return (
            <FormattedMessage
              id="login.error.social.missing.email.verified"
              defaultMessage={`We did not receive a valid email verification from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'social_login_unexpected_login_type':
          return (
            <FormattedMessage
              id="login.error.social.unexpected.login.type"
              defaultMessage={`We did not receive valid information from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'social_login_missing_slack_email_date_verified':
          return (
            <FormattedMessage
              id="login.error.slack.missing.email.date.verified"
              defaultMessage={`We did not receive a valid email date verified from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'social_login_invalid_social_id':
          return (
            <FormattedMessage
              id="login.error.social.invalid.social.id"
              defaultMessage={`We did not receive valid id from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'slack_login_invalid_team_id':
          return (
            <FormattedMessage
              id="login.error.slack.invalid.team.id"
              defaultMessage={`We did not receive valid team id from your identity provider. Please try logging in again by typing your email address. If you don't have a password, use the "Can't log in" link to set one.`}
            />
          );
        case 'social_login_managed_user':
          return (
            <FormattedMessage
              id="login.error.social.managed.user"
              defaultMessage="Hmm... we're having trouble logging you in. Please try again with a different authentication method."
            />
          );
        case 'social_login_requires_password_change':
          return (
            <FormattedMessage
              id="login.error.social.requires.password.change"
              defaultMessage="Your current password has expired, so you need to reset your password to continue."
            />
          );
        case 'social_login_hide_email_change_not_allowed_to_bypass_policy':
          return (
            <FormattedMessage
              id="login.error.social.hide.email.change.not.allowed.to.bypass.policy"
              defaultMessage="Sorry, you cannot hide your email once you are part of an organization. Please ensure that you share your real email."
            />
          );
        case 'social_login_email_change_not_allowed_to_bypass_policy':
          return (
            <FormattedMessage
              id="login.error.social.email.change.not.allowed.to.bypass.policy"
              defaultMessage="Sorry, we detected an email change that is not supported by your organization's authentication policy. Please log in with a email address that matches your account."
            />
          );
        case 'social_login_email_change_not_allowed':
          return (
            <FormattedMessage
              id="login.error.social.email.change.not.allowed"
              defaultMessage="We detected an email change that would remove you from your organization. Contact your organization admin to check your account is configured correctly or try to log in a different way."
            />
          );
        case 'social_login_consent_required':
          return (
            <FormattedMessage
              id="login.error.social.login.consent.required"
              defaultMessage="Please grant access to your account details if you wish to login to your Atlassian account."
            />
          );
        case 'user_is_blocked':
          return (
            <FormattedMessage
              id="login.error.user.blocked"
              defaultMessage="This account has been deactivated. Please contact your organization admin."
            />
          );
        case 'social_login_banned':
          return (
            <FormattedMessage
              id="login.error.social.login.banned"
              defaultMessage="Your admin has blocked third-party login. Log in with your email address instead."
            />
          );
        case 'apple_login_disabled':
          return (
            <FormattedMessage
              id="login.error.apple.login.disabled"
              defaultMessage="Sorry, it is not possible to log in with Apple at this time. We are working on it."
            />
          );
        case 'microsoft_login_disabled':
          return (
            <FormattedMessage
              id="login.error.microsoft.login.disabled"
              defaultMessage="Sorry, it is not possible to log in with Microsoft at this time. We are working on it."
            />
          );
        case 'slack_login_disabled':
          return (
            <FormattedMessage
              id="login.error.slack.login.disabled"
              defaultMessage="Sorry, it is not possible to log in with Slack at this time. We are working on it."
            />
          );
        case 'password_leaked':
          return passwordLeakedErrorMessage();
        case 'login.form.token.expired':
          return (
            <FormattedMessage
              id="login.error.token.expired"
              defaultMessage="Your login request has expired."
            />
          );
        case 'login.form.problem.encountered':
          return (
            <FormattedMessage
              id="login.error.problem.encountered"
              defaultMessage="We&#39;ve encountered a problem. Please try again in a few minutes."
            />
          );
        case 'login.form.email.does.not.match.loggedin.user':
          return (
            <FormattedMessage
              id="login.form.email.does.not.match.loggedin.user"
              defaultMessage="You're currently logged in as {username}, logging in as another user will log you out of your current session."
              values={error.args || {}}
            />
          );
        case 'hosted_domain_mismatch':
          return (
            <FormattedMessage
              id="google.hosted.domain.mismatch"
              defaultMessage="Unable to log in with Google. Contact your admin to check your account is configured correctly or try to log in a different way."
            />
          );
        case 'switch.session.expired':
          return (
            <FormattedMessage
              id="session.switch.error.expired"
              defaultMessage="Your account session expired. Log in again to continue."
            />
          );

        case 'request_error':
          return (
            <FormattedMessage
              id="network.request.error"
              defaultMessage="Unable to connect. Check your internet connection or the network settings for your browser and try again."
            />
          );

        case 'invalid.session.token':
          return (
            <FormattedMessage
              id="invalid.session.token"
              defaultMessage="Your session token is invalid. Log in again to continue."
            />
          );

        case 'verify.email.expired':
          return (
            <FormattedMessage
              id="verify.email.expired"
              defaultMessage="Your email verification attempt has expired. Enter your email address and try again."
            />
          );

        case 'verify.email.error':
          return (
            <FormattedMessage
              id="verify.email.error"
              defaultMessage="Something went wrong while verifying your email address. Enter your email address and try again."
            />
          );

        case 'too_many_username_requests':
          return (
            <FormattedMessage
              id="check.username.rate.limit.exceeded"
              defaultMessage="Too many login attempts detected. Please wait for a few minutes before trying again."
            />
          );

        case 'generic-consent-error':
          return (
            <FormattedMessage
              id="generic-consent-error"
              defaultMessage="Something went wrong while attempting to validate your request."
            />
          );

        case 'consent-validation-error':
          return (
            <FormattedMessage
              id="consent-validation-error"
              defaultMessage="Something went wrong while attempting to validate your consent. Try logging in again later."
            />
          );

        case 'consent-loading-error':
          return (
            <FormattedMessage
              id="consent-loading-error"
              defaultMessage="Something went wrong while loading the consent screen. Try logging in again later."
            />
          );

        default:
          return (
            <FormattedMessage
              id="login.error.unknown"
              defaultMessage="Something went wrong while attempting to validate your credentials."
            />
          );
      }
    }
  };

  useEffect(() => {
    analyticsClient.stopInitialLoadApdexEvent('viewLoginPage');
  }, [analyticsClient]);

  const prevIsCaptchaRequired = usePrevious(isCaptchaRequired);

  useEffect(() => {
    if (prevIsCaptchaRequired && !isCaptchaRequired) {
      setCaptchaValue(null);
    }
  }, [isCaptchaRequired, prevIsCaptchaRequired]);

  const emailError = (): string | undefined => {
    const error = fieldErrors.email;
    switch (error) {
      case 'unknown':
        return intl.formatMessage(messages.unexpectedErrorHappened);
      case 'rate_limit_exceeded_message':
        return intl.formatMessage(messages.rateLimitErrorMessage);
      default:
        return error;
    }
  };

  const renderExperimentButtons = () => {
    if (redirectType === CheckUserNameRedirectType.Sso) {
      return (
        <OpeningSso>
          <FormattedMessage
            id="login.form.opening.sso"
            defaultMessage="Opening your single sign-on provider"
          />
          <Spinner />
        </OpeningSso>
      );
    } else {
      return (
        <TallButton
          appearance="primary"
          id="login-submit"
          type="submit"
          isFullWidth
          isDisabled={isSecondStep && isCaptchaRequired && !captchaValue}
          isLoading={
            buttonClicked === 'submit' &&
            (!!isRedirecting || isLoading || isCheckUsernameCaptchaLoading)
          }>
          {buttonContent()}
        </TallButton>
      );
    }
  };

  const shouldRenderCheckUsernameRecaptcha =
    isCheckUsernameRecaptchaEnabled && !isSecondStep && recaptcha.enabled && recaptcha.component;

  const shouldRenderV2Recaptcha = isSecondStep && isCaptchaRequired;

  const { formatMessage } = intl;
  return (
    <div>
      {isErrorShown && <FormError>{errorContent()}</FormError>}
      <span> {infoContent()} </span>
      <Form id="form-login" data-testid="form-login" onSubmit={handleSubmit} onClick={onFormClick}>
        <InputsWrapper>
          {restrictedEmail || redirectType === CheckUserNameRedirectType.Sso ? (
            <RestrictedEmailContainer value={email} />
          ) : (
            <EmailContainer
              isReadOnly={isSecondStep || isLoading}
              fieldError={emailError()}
              onChange={onEmailChange}
              value={email}
              onEmailClick={onEmailClick}
              placeholderOverride={formatMessage(messages.emailPlaceholder)}
              inputRef={inputRef}
            />
          )}
          <PasswordContainer
            isHidden={!isSecondStep}
            fieldError={fieldErrors.password}
            onChange={handlePasswordChange}
            value={password}
            className={`field-group password-field ${isSecondStep ? 'expanded' : 'hidden'}`}
          />

          {isCaptchaEnabledByConfig &&
            (shouldRenderCheckUsernameRecaptcha ? (
              <ReCaptchaContainer>{recaptcha.component}</ReCaptchaContainer>
            ) : (
              shouldRenderV2Recaptcha && (
                <ReCaptchaContainer>
                  <ReCAPTCHAEnterprise
                    sitekey={recaptchaEnterpriseCheckboxKeySite}
                    onChange={handleCaptchaChange}
                  />
                </ReCaptchaContainer>
              )
            ))}

          {isSecondStep && (
            <StyledMarketingConsent
              config={marketingConsentConfig}
              onConsentChange={handleMarketingConsentChange}
            />
          )}
        </InputsWrapper>
        {renderExperimentButtons()}
        <FieldGroup isHidden={!isSecondStep || !isPasswordlessAllowed}>
          <StyledSocialLoginButton
            id="passwordless-button"
            iconBefore={<EmailIcon label="email" />}
            iconAfter={<span style={{ width: 24 }} />} // to center text
            isFullWidth={true}
            advisoryEnabled={false}
            onClick={handlePasswordlessButtonClick}
            isLoading={buttonClicked === 'passwordless' && (!!isRedirecting || isLoading)}
            isDisabled={(isCaptchaRequired && !captchaValue) || isLoading || isRedirecting}>
            <FormattedMessage
              id="login.form.passwordless.button"
              defaultMessage="Log in with email"
            />
          </StyledSocialLoginButton>
        </FieldGroup>
      </Form>
    </div>
  );
};

export default LoginForm;
